import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI } from '../api/admin.api';
import toast from 'react-hot-toast';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Forgot Password States
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1); // 1: Email, 2: OTP, 3: New Password
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fpLoading, setFpLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await adminAPI.login(formData);
      login(
          response.data, 
          response.data.token, 'admin');
      toast.success('Login successful');
      navigate('/admin/athletes');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // Forgot Password - Step 1: Send OTP
  const handleSendOTP = async () => {
    if (!forgotPasswordEmail) {
      toast.error('Please enter your email');
      return;
    }

    setFpLoading(true);
    try {
      await adminAPI.forgotPassword(forgotPasswordEmail);
      toast.success('OTP sent to your email');
      setForgotPasswordStep(2);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send OTP');
    } finally {
      setFpLoading(false);
    }
  };

  // Forgot Password - Step 2: Verify OTP
  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setFpLoading(true);
    try {
      const response = await adminAPI.verifyResetOTP(forgotPasswordEmail, otp);
      setResetToken(response.data.resetToken);
      toast.success('OTP verified successfully');
      setForgotPasswordStep(3);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Invalid OTP');
    } finally {
      setFpLoading(false);
    }
  };

  // Forgot Password - Step 3: Reset Password
  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setFpLoading(true);
    try {
      await adminAPI.resetPassword(forgotPasswordEmail, resetToken, newPassword);
      toast.success('Password reset successfully. Please login.');
      setShowForgotPassword(false);
      resetForgotPasswordState();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reset password');
    } finally {
      setFpLoading(false);
    }
  };

  const resetForgotPasswordState = () => {
    setForgotPasswordStep(1);
    setForgotPasswordEmail('');
    setOtp('');
    setResetToken('');
    setNewPassword('');
    setConfirmPassword('');
  };

  return (
    <Box
      sx={{
        minHeight: '80vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={10} sx={{ p: 4, borderRadius: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
              Admin Login
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Access the admin dashboard
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              required
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ textAlign: 'right', mt: 1 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => setShowForgotPassword(true)}
                sx={{ textDecoration: 'none' }}
              >
                Forgot Password?
              </Link>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ mt: 3, py: 1.5 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Login'}
            </Button>
          </form>
        </Paper>
      </Container>

      {/* Forgot Password Dialog */}
      <Dialog
        open={showForgotPassword}
        onClose={() => {
          setShowForgotPassword(false);
          resetForgotPasswordState();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reset Admin Password</DialogTitle>
        <DialogContent>
          {/* Step 1: Enter Email */}
          {forgotPasswordStep === 1 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Enter your admin email address to receive an OTP for password reset.
              </Typography>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}

          {/* Step 2: Enter OTP */}
          {forgotPasswordStep === 2 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Enter the 6-digit OTP sent to <strong>{forgotPasswordEmail}</strong>
              </Typography>
              <TextField
                fullWidth
                label="OTP"
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                margin="normal"
                placeholder="Enter 6-digit OTP"
                inputProps={{ maxLength: 6 }}
              />
              <Button
                variant="text"
                size="small"
                onClick={() => setForgotPasswordStep(1)}
                sx={{ mt: 1 }}
              >
                Change Email
              </Button>
            </Box>
          )}

          {/* Step 3: Set New Password */}
          {forgotPasswordStep === 3 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Set a new password for your admin account
              </Typography>
              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                margin="normal"
                helperText="Minimum 8 characters"
              />
              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="normal"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowForgotPassword(false);
              resetForgotPasswordState();
            }}
            disabled={fpLoading}
          >
            Cancel
          </Button>
          
          {forgotPasswordStep === 1 && (
            <Button
              onClick={handleSendOTP}
              variant="contained"
              disabled={fpLoading}
            >
              {fpLoading ? <CircularProgress size={20} /> : 'Send OTP'}
            </Button>
          )}
          
          {forgotPasswordStep === 2 && (
            <Button
              onClick={handleVerifyOTP}
              variant="contained"
              disabled={fpLoading}
            >
              {fpLoading ? <CircularProgress size={20} /> : 'Verify OTP'}
            </Button>
          )}
          
          {forgotPasswordStep === 3 && (
            <Button
              onClick={handleResetPassword}
              variant="contained"
              disabled={fpLoading}
            >
              {fpLoading ? <CircularProgress size={20} /> : 'Reset Password'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminLogin;